/**
 * Scene venues — fixed-coordinate landmarks layered on top of the map.
 *
 * Unlike profiles (which orbit the user via projected mapX/mapY), venues
 * sit at their real lat/lng. A pin only appears when the user pans into
 * its part of the world. Seeded with prominent rooms across the major
 * scene cities so the map is never empty regardless of where the user is.
 *
 * Coordinates are deliberately rounded to ~5dp — close enough for a
 * map pin, never precise enough to read as a doxx.
 */

export type VenueKind =
  | 'bar'
  | 'club'
  | 'cabaret'
  | 'sauna'
  | 'cruise'
  | 'cafe'
  | 'landmark';

export interface Venue {
  id: string;
  name: string;
  kind: VenueKind;
  city: string;
  /** Country flag emoji or 2-letter code — used in the bottom sheet eyebrow. */
  country: string;
  lat: number;
  lng: number;
  /** Short editorial line — keep under ~80 chars. */
  blurb: string;
  /** Optional outbound link. */
  url?: string;
}

/**
 * Display label for each kind — brand-safe, lowercase, sharp.
 * Used in marker tooltips and the venue sheet eyebrow.
 */
export const VENUE_KIND_LABEL: Record<VenueKind, string> = {
  bar: 'bar',
  club: 'club',
  cabaret: 'cabaret',
  sauna: 'sauna',
  cruise: 'cruise',
  cafe: 'cafe',
  landmark: 'landmark',
};

/**
 * Material Symbols glyph for each kind. Picked for legibility at 12px.
 */
export const VENUE_KIND_ICON: Record<VenueKind, string> = {
  bar: 'local_bar',
  club: 'nightlife',
  cabaret: 'theater_comedy',
  sauna: 'hot_tub',
  cruise: 'door_open',
  cafe: 'local_cafe',
  landmark: 'star',
};

export const VENUES: Venue[] = [
  /* ---------------- MELBOURNE ---------------- */
  {
    id: 'mel-sircuit',
    name: 'Sircuit',
    kind: 'bar',
    city: 'Melbourne',
    country: 'AU',
    lat: -37.81735,
    lng: 144.9802,
    blurb: 'Smith Street fixture. Drag, dance, late.',
  },
  {
    id: 'mel-dts',
    name: 'DT’s Hotel',
    kind: 'bar',
    city: 'Melbourne',
    country: 'AU',
    lat: -37.82531,
    lng: 144.99462,
    blurb: 'Richmond corner pub. Beers, beards, bingo.',
  },
  {
    id: 'mel-laird',
    name: 'The Laird',
    kind: 'bar',
    city: 'Melbourne',
    country: 'AU',
    lat: -37.79937,
    lng: 144.98318,
    blurb: 'Australia’s longest-running men-only room.',
  },
  {
    id: 'mel-poofdoof',
    name: 'Poof Doof',
    kind: 'club',
    city: 'Melbourne',
    country: 'AU',
    lat: -37.81591,
    lng: 144.96914,
    blurb: 'Saturdays at Circus. Pop, sweat, glitter.',
  },
  {
    id: 'mel-club80',
    name: 'Club 80',
    kind: 'cruise',
    city: 'Melbourne',
    country: 'AU',
    lat: -37.81088,
    lng: 144.98069,
    blurb: 'Collingwood basement. Dark, hot, late.',
  },
  {
    id: 'mel-wetonwellington',
    name: 'Wet on Wellington',
    kind: 'sauna',
    city: 'Melbourne',
    country: 'AU',
    lat: -37.80875,
    lng: 144.98214,
    blurb: '24/7. Steam, sling, sauna.',
  },

  /* ---------------- SYDNEY ---------------- */
  {
    id: 'syd-stonewall',
    name: 'Stonewall',
    kind: 'bar',
    city: 'Sydney',
    country: 'AU',
    lat: -33.87902,
    lng: 151.21372,
    blurb: 'Oxford Street institution. Three floors, every night.',
  },
  {
    id: 'syd-universal',
    name: 'Universal',
    kind: 'club',
    city: 'Sydney',
    country: 'AU',
    lat: -33.87858,
    lng: 151.21253,
    blurb: 'Big-room pop nights, drag, weekenders.',
  },
  {
    id: 'syd-palms',
    name: 'Palms on Oxford',
    kind: 'club',
    city: 'Sydney',
    country: 'AU',
    lat: -33.87878,
    lng: 151.21199,
    blurb: '80s downstairs, sing-alongs, no judgement.',
  },
  {
    id: 'syd-beresford',
    name: 'The Beresford',
    kind: 'bar',
    city: 'Sydney',
    country: 'AU',
    lat: -33.88698,
    lng: 151.21442,
    blurb: 'Beer garden Sundays. Pretty crowd.',
  },
  {
    id: 'syd-kenscitysteam',
    name: 'Ken’s of Kensington',
    kind: 'sauna',
    city: 'Sydney',
    country: 'AU',
    lat: -33.90873,
    lng: 151.22425,
    blurb: 'Long-running steam since the 80s.',
  },

  /* ---------------- LONDON ---------------- */
  {
    id: 'lon-heaven',
    name: 'Heaven',
    kind: 'club',
    city: 'London',
    country: 'GB',
    lat: 51.50783,
    lng: -0.12423,
    blurb: 'Under the arches. The benchmark since 1979.',
  },
  {
    id: 'lon-gaybar',
    name: 'G-A-Y Bar',
    kind: 'bar',
    city: 'London',
    country: 'GB',
    lat: 51.51326,
    lng: -0.13145,
    blurb: 'Soho corner. Pop, cheap shots, chaos.',
  },
  {
    id: 'lon-rvt',
    name: 'Royal Vauxhall Tavern',
    kind: 'cabaret',
    city: 'London',
    country: 'GB',
    lat: 51.48565,
    lng: -0.1175,
    blurb: 'Heritage cabaret room. Drag, alt, listed.',
  },
  {
    id: 'lon-eagle',
    name: 'The Eagle London',
    kind: 'bar',
    city: 'London',
    country: 'GB',
    lat: 51.48298,
    lng: -0.1206,
    blurb: 'Vauxhall leather/bear room. Sunday horse meat.',
  },
  {
    id: 'lon-twobrewers',
    name: 'Two Brewers',
    kind: 'cabaret',
    city: 'London',
    country: 'GB',
    lat: 51.46332,
    lng: -0.1393,
    blurb: 'Clapham drag mecca. Always sold out.',
  },
  {
    id: 'lon-sweatbox',
    name: 'Sweatbox',
    kind: 'sauna',
    city: 'London',
    country: 'GB',
    lat: 51.51389,
    lng: -0.13231,
    blurb: 'Soho. 24/7. Steam in the centre.',
  },

  /* ---------------- NEW YORK ---------------- */
  {
    id: 'nyc-stonewall',
    name: 'Stonewall Inn',
    kind: 'landmark',
    city: 'New York',
    country: 'US',
    lat: 40.73393,
    lng: -74.00216,
    blurb: 'Christopher Street. National monument.',
  },
  {
    id: 'nyc-eagle',
    name: 'The Eagle NYC',
    kind: 'bar',
    city: 'New York',
    country: 'US',
    lat: 40.7506,
    lng: -74.0049,
    blurb: 'Chelsea rooftop. Leather, fetish, harness.',
  },
  {
    id: 'nyc-boxers',
    name: 'Boxers HK',
    kind: 'bar',
    city: 'New York',
    country: 'US',
    lat: 40.76156,
    lng: -73.99394,
    blurb: 'Sports bar that happens to be a gay bar.',
  },
  {
    id: 'nyc-industry',
    name: 'Industry Bar',
    kind: 'bar',
    city: 'New York',
    country: 'US',
    lat: 40.76235,
    lng: -73.98807,
    blurb: 'Hell’s Kitchen. Drag every night.',
  },
  {
    id: 'nyc-thecock',
    name: 'The Cock',
    kind: 'club',
    city: 'New York',
    country: 'US',
    lat: 40.72439,
    lng: -73.98352,
    blurb: 'East Village late room. Sticky, loud, ours.',
  },

  /* ---------------- SAN FRANCISCO ---------------- */
  {
    id: 'sf-eagle',
    name: 'SF Eagle',
    kind: 'bar',
    city: 'San Francisco',
    country: 'US',
    lat: 37.7705,
    lng: -122.41394,
    blurb: 'SOMA leather landmark. Patio Sundays.',
  },
  {
    id: 'sf-powerhouse',
    name: 'Powerhouse',
    kind: 'club',
    city: 'San Francisco',
    country: 'US',
    lat: 37.77234,
    lng: -122.41372,
    blurb: 'Folsom-adjacent. Cruise, leather, late.',
  },
  {
    id: 'sf-twinpeaks',
    name: 'Twin Peaks Tavern',
    kind: 'landmark',
    city: 'San Francisco',
    country: 'US',
    lat: 37.76238,
    lng: -122.43525,
    blurb: 'Castro corner. Glass windows, since ’72.',
  },
  {
    id: 'sf-midnightsun',
    name: 'Midnight Sun',
    kind: 'bar',
    city: 'San Francisco',
    country: 'US',
    lat: 37.76155,
    lng: -122.43492,
    blurb: 'Castro video bar. Pop, drag, drinks.',
  },

  /* ---------------- LOS ANGELES ---------------- */
  {
    id: 'la-abbey',
    name: 'The Abbey',
    kind: 'club',
    city: 'Los Angeles',
    country: 'US',
    lat: 34.08406,
    lng: -118.38545,
    blurb: 'WeHo. Patios, bottle service, the room.',
  },
  {
    id: 'la-akbar',
    name: 'Akbar',
    kind: 'bar',
    city: 'Los Angeles',
    country: 'US',
    lat: 34.09008,
    lng: -118.27437,
    blurb: 'Silver Lake. Mixed, alt, dancing in the back.',
  },
  {
    id: 'la-eagle',
    name: 'Eagle LA',
    kind: 'bar',
    city: 'Los Angeles',
    country: 'US',
    lat: 34.09161,
    lng: -118.28189,
    blurb: 'Silver Lake leather. Beards, harnesses, patio.',
  },
  {
    id: 'la-mickeys',
    name: 'Mickey’s WeHo',
    kind: 'club',
    city: 'Los Angeles',
    country: 'US',
    lat: 34.0828,
    lng: -118.38567,
    blurb: 'Santa Monica Blvd. Go-go, drag, dance.',
  },

  /* ---------------- BERLIN ---------------- */
  {
    id: 'ber-berghain',
    name: 'Berghain',
    kind: 'club',
    city: 'Berlin',
    country: 'DE',
    lat: 52.51115,
    lng: 13.4432,
    blurb: 'Friedrichshain power plant. Saturday into Monday.',
  },
  {
    id: 'ber-schwuz',
    name: 'SchwuZ',
    kind: 'club',
    city: 'Berlin',
    country: 'DE',
    lat: 52.48211,
    lng: 13.43018,
    blurb: 'Neukölln long-runner. Pop, drag, parties.',
  },
  {
    id: 'ber-toms',
    name: 'Tom’s Bar',
    kind: 'cruise',
    city: 'Berlin',
    country: 'DE',
    lat: 52.49856,
    lng: 13.34939,
    blurb: 'Schöneberg basement. Open since ’81.',
  },
  {
    id: 'ber-connection',
    name: 'Connection Club',
    kind: 'club',
    city: 'Berlin',
    country: 'DE',
    lat: 52.49932,
    lng: 13.34902,
    blurb: 'Two floors, maze downstairs.',
  },

  /* ---------------- PARIS ---------------- */
  {
    id: 'par-depot',
    name: 'Le Dépôt',
    kind: 'club',
    city: 'Paris',
    country: 'FR',
    lat: 48.86379,
    lng: 2.3556,
    blurb: 'Marais megaclub. Dance up, cruise down.',
  },
  {
    id: 'par-cox',
    name: 'Cox',
    kind: 'bar',
    city: 'Paris',
    country: 'FR',
    lat: 48.85788,
    lng: 2.35577,
    blurb: 'Marais corner. Aperitif crowd spilling out.',
  },
  {
    id: 'par-freedj',
    name: 'Freedj',
    kind: 'bar',
    city: 'Paris',
    country: 'FR',
    lat: 48.86245,
    lng: 2.35403,
    blurb: 'Late Marais. Pop, smoke, neon.',
  },

  /* ---------------- AMSTERDAM ---------------- */
  {
    id: 'ams-soho',
    name: 'Soho',
    kind: 'bar',
    city: 'Amsterdam',
    country: 'NL',
    lat: 52.36534,
    lng: 4.88958,
    blurb: 'Reguliersdwarsstraat staple. Wall-to-wall.',
  },
  {
    id: 'ams-prik',
    name: 'Prik',
    kind: 'bar',
    city: 'Amsterdam',
    country: 'NL',
    lat: 52.37486,
    lng: 4.88698,
    blurb: 'Spuistraat. Prosecco, pop, terrace.',
  },
  {
    id: 'ams-clubchurch',
    name: 'Club Church',
    kind: 'club',
    city: 'Amsterdam',
    country: 'NL',
    lat: 52.36458,
    lng: 4.88863,
    blurb: 'Themed cruise nights. Members welcome.',
  },

  /* ---------------- MADRID ---------------- */
  {
    id: 'mad-llshow',
    name: 'LL Show Bar',
    kind: 'cabaret',
    city: 'Madrid',
    country: 'ES',
    lat: 40.4218,
    lng: -3.69973,
    blurb: 'Chueca. Drag shows every night.',
  },
  {
    id: 'mad-acuarela',
    name: 'Café Acuarela',
    kind: 'cafe',
    city: 'Madrid',
    country: 'ES',
    lat: 40.42221,
    lng: -3.69854,
    blurb: 'Plaza Chueca corner. Cocktails on the square.',
  },

  /* ---------------- BARCELONA ---------------- */
  {
    id: 'bcn-arena',
    name: 'Arena Madre',
    kind: 'club',
    city: 'Barcelona',
    country: 'ES',
    lat: 41.39148,
    lng: 2.16365,
    blurb: 'Gaixample. Pop and reggaeton till sunrise.',
  },
  {
    id: 'bcn-punto',
    name: 'Punto BCN',
    kind: 'bar',
    city: 'Barcelona',
    country: 'ES',
    lat: 41.3892,
    lng: 2.16365,
    blurb: 'After-work pints. Locals only feel.',
  },

  /* ---------------- TORONTO ---------------- */
  {
    id: 'tor-woodys',
    name: 'Woody’s',
    kind: 'bar',
    city: 'Toronto',
    country: 'CA',
    lat: 43.66524,
    lng: -79.38222,
    blurb: 'Church St cornerstone. Best chest contest, since forever.',
  },
  {
    id: 'tor-crews',
    name: 'Crews & Tangos',
    kind: 'cabaret',
    city: 'Toronto',
    country: 'CA',
    lat: 43.66534,
    lng: -79.38217,
    blurb: 'Two floors of drag. Always packed.',
  },
  {
    id: 'tor-blackeagle',
    name: 'Black Eagle',
    kind: 'bar',
    city: 'Toronto',
    country: 'CA',
    lat: 43.66666,
    lng: -79.38149,
    blurb: 'Leather, denim, rooftop.',
  },

  /* ---------------- CHICAGO ---------------- */
  {
    id: 'chi-sidetrack',
    name: 'Sidetrack',
    kind: 'bar',
    city: 'Chicago',
    country: 'US',
    lat: 41.94735,
    lng: -87.64915,
    blurb: 'Boystown. Showtunes Sundays, six rooms.',
  },
  {
    id: 'chi-roscoes',
    name: 'Roscoe’s',
    kind: 'bar',
    city: 'Chicago',
    country: 'US',
    lat: 41.94747,
    lng: -87.64923,
    blurb: 'Dance floor and dog park rolled together.',
  },
];
